/* Build a CloudDream shell around eSolver's existing OTP controls.
   The script moves genuine app nodes but never replaces OTP or checkout behaviour. */
(function () {
  'use strict';

  var ROOT_ID = 'otp-overlay';
  var CONFIG_ID = 'CloudDreamEsolverOtpConfig';
  var SHELL_ATTRIBUTE = 'data-cd-esolver-shell';
  var scheduled = false;
  var observedRoot = null;
  var rootObserver = null;
  var lastVisible = false;
  var summaryRequestId = 0;
  var triggerProductForm = null;
  var config = typeof document === 'undefined' ? {} : readConfig();
  var moneyFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
  var savingsFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  function readConfig() {
    var node = document.getElementById(CONFIG_ID);
    if (!node) return {};

    try {
      return JSON.parse(node.textContent || '{}');
    } catch {
      return {};
    }
  }

  function createElement(tagName, className, text) {
    var element = document.createElement(tagName);
    if (className) element.className = className;
    if (text != null) element.textContent = text;
    return element;
  }

  function createTrustItem(label) {
    var item = createElement('div', 'cd-esolver-trust__item');
    var icon = createElement('span', 'cd-esolver-trust__icon', '✓');
    icon.setAttribute('aria-hidden', 'true');
    item.appendChild(icon);
    item.appendChild(createElement('span', 'cd-esolver-trust__label', label));
    return item;
  }

  function createHeader(closeButton) {
    var header = createElement('div', 'cd-esolver-header');
    header.appendChild(closeButton);
    header.appendChild(createElement('div', 'cd-esolver-header__brand', config.brand || 'CloudDream'));
    header.appendChild(createElement('span', 'cd-esolver-header__balance'));
    return header;
  }

  function formatMoney(cents) {
    var amount = Math.round((Number(cents) || 0) / 100);
    return '৳' + moneyFormatter.format(amount);
  }

  function formatSavingsAmount(cents) {
    return savingsFormatter.format((Number(cents) || 0) / 100);
  }

  function countLabel(count) {
    var template = count === 1 ? config.itemCountOne : config.itemCountOther;
    return String(template || '').replace('__COUNT__', String(count));
  }

  function selectedProductForm(allowFallback) {
    var active = document.activeElement;
    var form = active && active.closest ? active.closest('form[action*="/cart/add"]') : null;
    if (!form && triggerProductForm && triggerProductForm.isConnected) form = triggerProductForm;
    if (form || !allowFallback || !config.product) return form;

    return Array.prototype.find.call(document.querySelectorAll('form[action*="/cart/add"]'), function (candidate) {
      var bounds = candidate.getBoundingClientRect();
      return bounds.width > 0 && bounds.height > 0;
    }) || null;
  }

  function pendingProductLine(allowFallback) {
    var form = selectedProductForm(allowFallback);
    if (!allowFallback) triggerProductForm = null;
    if (!form || !config.product || !Array.isArray(config.product.variants)) return null;

    var variantInput = form.querySelector('[name="id"]');
    if (!variantInput) return null;

    var variantId = Number(variantInput.value);
    var variant = config.product.variants.find(function (candidate) { return Number(candidate.id) === variantId; });
    if (!variant) return null;

    var quantityInput = form.querySelector('[name="quantity"]');
    var quantity = Math.max(1, Number.parseInt(quantityInput && quantityInput.value, 10) || 1);
    return {
      image: variant.image || config.product.image || '',
      originalLinePrice: Math.max(variant.compareAtPrice || 0, variant.price || 0) * quantity,
      finalLinePrice: (variant.price || 0) * quantity,
      productTitle: config.product.title,
      quantity: quantity,
      url: config.product.url,
      variantTitle: variant.title === 'Default Title' ? '' : variant.title,
    };
  }

  function cartLine(item) {
    var featuredImage = item.featured_image && item.featured_image.url;
    return {
      image: featuredImage || item.image || '',
      originalLinePrice: Number(item.original_line_price) || Number(item.line_price) || 0,
      finalLinePrice: Number(item.line_price) || Number(item.final_line_price) || 0,
      productTitle: item.product_title || item.title || '',
      quantity: Number(item.quantity) || 0,
      url: item.url || '',
      variantTitle: item.variant_title === 'Default Title' ? '' : item.variant_title || '',
    };
  }

  function cartDiscounts(cart) {
    var discountsByTitle = new Map();

    function addDiscount(title, amount) {
      var value = Math.max(0, Number(amount) || 0);
      if (!value) return;
      var key = title || '';
      discountsByTitle.set(key, (discountsByTitle.get(key) || 0) + value);
    }

    (Array.isArray(cart.items) ? cart.items : []).forEach(function (item) {
      (Array.isArray(item.discounts) ? item.discounts : []).forEach(function (discount) {
        addDiscount(discount.title, discount.amount);
      });
    });

    var totalDiscount = Math.max(0, Number(cart.total_discount) || 0);
    var allocated = Array.from(discountsByTitle.values()).reduce(function (total, amount) {
      return total + amount;
    }, 0);

    if (allocated < totalDiscount) {
      (Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : [])
        .forEach(function (discount) {
          if (!discountsByTitle.has(discount.title || '')) {
            addDiscount(discount.title, discount.total_allocated_amount);
          }
        });
    }

    var discounts = Array.from(discountsByTitle, function (entry) {
      return { title: entry[0], amount: entry[1] };
    });
    allocated = discounts.reduce(function (total, discount) { return total + discount.amount; }, 0);

    if (allocated > totalDiscount || (!discounts.length && totalDiscount)) {
      return totalDiscount ? [{ title: '', amount: totalDiscount }] : [];
    }
    if (allocated < totalDiscount) discounts.push({ title: '', amount: totalDiscount - allocated });
    return discounts;
  }

  function summaryModel(cart, pendingLine) {
    var lines = Array.isArray(cart.items) ? cart.items.map(cartLine) : [];
    var itemCount = Number(cart.item_count) || 0;
    var subtotal = Number(cart.original_total_price) || 0;
    var discount = Number(cart.total_discount) || 0;
    var total = Number(cart.total_price) || 0;

    if (pendingLine) {
      lines.push(pendingLine);
      itemCount += pendingLine.quantity;
      subtotal += pendingLine.finalLinePrice;
      total += pendingLine.finalLinePrice;
    }

    return {
      discount: discount,
      discounts: cartDiscounts(cart),
      estimated: Boolean(pendingLine),
      itemCount: itemCount,
      lines: lines,
      subtotal: subtotal,
      total: total,
    };
  }

  function offerSummary(model) {
    if (!model || model.discount <= 0) return null;
    var discounts = (Array.isArray(model.discounts) ? model.discounts : []).filter(function (discount) {
      return Number(discount.amount) > 0;
    });
    return {
      amount: model.discount,
      count: discounts.length || 1,
      title: discounts.length === 1 ? discounts[0].title || '' : '',
    };
  }

  function createBillRow(className, label) {
    var row = createElement('div', 'cd-esolver-bill__row ' + className);
    row.appendChild(createElement('span', 'cd-esolver-bill__label', label));
    row.appendChild(createElement('span', 'cd-esolver-bill__value'));
    return row;
  }

  function createOrderSummary() {
    var details = createElement('details', 'cd-esolver-summary');
    var summary = createElement('summary', 'cd-esolver-summary-card');
    var top = createElement('span', 'cd-esolver-summary-card__top');
    var heading = createElement('span', 'cd-esolver-summary-card__heading');
    heading.appendChild(createElement('strong', 'cd-esolver-summary-card__title', config.summaryHeading));
    heading.appendChild(createElement('span', 'cd-esolver-summary-card__count'));
    var prices = createElement('span', 'cd-esolver-summary-card__prices');
    prices.appendChild(createElement('span', 'cd-esolver-summary-card__total', config.summaryLoading));
    prices.appendChild(createElement('s', 'cd-esolver-summary-card__original'));
    prices.appendChild(createElement('span', 'cd-esolver-summary-card__chevron', '›'));
    top.appendChild(heading);
    top.appendChild(prices);
    summary.appendChild(top);
    summary.appendChild(createElement('span', 'cd-esolver-summary-card__offer'));
    summary.appendChild(createElement('span', 'cd-esolver-summary-card__savings'));

    var backdrop = createElement('button', 'cd-esolver-summary-backdrop');
    backdrop.type = 'button';
    backdrop.setAttribute('aria-label', config.closeSummary);
    var sheet = createElement('section', 'cd-esolver-summary-sheet');
    sheet.setAttribute('aria-label', config.summaryHeading);
    var sheetCloseButton = createElement('button', 'cd-esolver-summary-sheet__close', '×');
    sheetCloseButton.type = 'button';
    sheetCloseButton.setAttribute('aria-label', config.closeSummary);
    sheetCloseButton.addEventListener('click', function () {
      details.open = false;
      summary.focus();
    });
    var sheetHeader = createElement('header', 'cd-esolver-summary-sheet__header');
    sheetHeader.appendChild(createElement('strong', 'cd-esolver-summary-sheet__title', config.summaryHeading));
    sheetHeader.appendChild(createElement('span', 'cd-esolver-summary-sheet__count'));
    var sheetBody = createElement('div', 'cd-esolver-summary-sheet__body');
    var items = createElement('div', 'cd-esolver-summary-items');
    var bill = createElement('div', 'cd-esolver-bill');
    bill.appendChild(createElement('h4', 'cd-esolver-bill__heading', config.billHeading));
    bill.appendChild(createBillRow('cd-esolver-bill__row--subtotal', config.subtotalLabel));
    bill.appendChild(createElement('div', 'cd-esolver-bill__discounts'));
    bill.appendChild(createBillRow('cd-esolver-bill__row--shipping', config.shippingLabel));
    bill.appendChild(createBillRow('cd-esolver-bill__row--total', config.totalLabel));
    bill.appendChild(createElement('p', 'cd-esolver-bill__note'));
    sheetBody.appendChild(items);
    sheetBody.appendChild(bill);
    sheetHeader.appendChild(sheetCloseButton);
    sheet.appendChild(sheetHeader);
    sheet.appendChild(sheetBody);
    details.appendChild(summary);
    details.appendChild(backdrop);
    details.appendChild(sheet);
    details.dataset.summaryStatus = 'loading';
    backdrop.addEventListener('click', function () { details.open = false; });
    details.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && details.open) {
        details.open = false;
        summary.focus();
      }
    });
    details.addEventListener('toggle', function () {
      if (details.open && observedRoot) refreshSummary(observedRoot);
    });
    return details;
  }

  function createSummaryItem(line) {
    var item = createElement('div', 'cd-esolver-summary-item');
    if (line.image) {
      var image = createElement('img', 'cd-esolver-summary-item__image');
      image.src = line.image;
      image.alt = line.productTitle;
      image.loading = 'lazy';
      item.appendChild(image);
    } else {
      item.appendChild(createElement('span', 'cd-esolver-summary-item__image cd-esolver-summary-item__image--empty'));
    }

    var copy = createElement('div', 'cd-esolver-summary-item__copy');
    if (line.url) {
      var link = createElement('a', 'cd-esolver-summary-item__title', line.productTitle);
      link.href = line.url;
      copy.appendChild(link);
    } else {
      copy.appendChild(createElement('span', 'cd-esolver-summary-item__title', line.productTitle));
    }
    if (line.variantTitle) copy.appendChild(createElement('span', 'cd-esolver-summary-item__variant', line.variantTitle));
    copy.appendChild(createElement('span', 'cd-esolver-summary-item__quantity', config.quantityLabel.replace('__COUNT__', String(line.quantity))));
    item.appendChild(copy);

    var prices = createElement('div', 'cd-esolver-summary-item__prices');
    prices.appendChild(createElement('span', 'cd-esolver-summary-item__final', formatMoney(line.finalLinePrice)));
    var original = createElement('s', 'cd-esolver-summary-item__original', formatMoney(line.originalLinePrice));
    original.hidden = line.originalLinePrice <= line.finalLinePrice;
    prices.appendChild(original);
    item.appendChild(prices);
    return item;
  }

  function setBillValue(summary, rowClass, value) {
    var valueNode = summary.querySelector(rowClass + ' .cd-esolver-bill__value');
    if (valueNode) valueNode.textContent = value;
  }

  function renderDiscounts(summary, discounts) {
    var container = summary.querySelector('.cd-esolver-bill__discounts');
    if (!container) return;

    container.replaceChildren.apply(container, discounts.map(function (discount) {
      var label = discount.title
        ? config.discountNamedLabel.replace('__TITLE__', discount.title)
        : config.discountLabel;
      var row = createBillRow('cd-esolver-bill__row--discount', label);
      row.querySelector('.cd-esolver-bill__value').textContent = '−' + formatMoney(discount.amount);
      return row;
    }));
    container.hidden = discounts.length === 0;
  }

  function renderOfferSummary(summary, model) {
    var offer = summary.querySelector('.cd-esolver-summary-card__offer');
    var savings = summary.querySelector('.cd-esolver-summary-card__savings');
    var state = offerSummary(model);
    if (!offer || !savings) return;

    offer.hidden = !state;
    savings.hidden = !state;
    if (!state) return;

    var amount = 'BDT ' + formatSavingsAmount(state.amount);
    var template = state.count > 1
      ? config.multipleOffersLabel
      : state.title
        ? config.singleNamedOfferLabel.replace('__TITLE__', state.title)
        : config.singleOfferLabel;
    offer.textContent = template.replace('__AMOUNT__', amount);
    var savingsTemplate = config.savingsSoFarLabel.replace(/&#39;|&apos;/g, "'");
    var savingsParts = savingsTemplate.split('__AMOUNT__');
    savings.replaceChildren(
      document.createTextNode(savingsParts[0] || ''),
      createElement('strong', 'cd-esolver-summary-card__savings-amount', amount),
      document.createTextNode(savingsParts.slice(1).join('__AMOUNT__'))
    );
  }

  function renderSummary(root, model) {
    var summary = root.querySelector('.cd-esolver-summary');
    if (!summary) return;

    var label = countLabel(model.itemCount);
    summary.dataset.summaryStatus = model.estimated ? 'estimated' : 'exact';
    summary.querySelector('.cd-esolver-summary-card__count').textContent = label;
    summary.querySelector('.cd-esolver-summary-sheet__count').textContent = label;
    summary.querySelector('.cd-esolver-summary-card__total').textContent = formatMoney(model.total);
    var original = summary.querySelector('.cd-esolver-summary-card__original');
    original.textContent = formatMoney(model.subtotal);
    original.hidden = model.subtotal <= model.total;
    renderOfferSummary(summary, model);

    var items = summary.querySelector('.cd-esolver-summary-items');
    items.replaceChildren.apply(items, model.lines.map(createSummaryItem));
    setBillValue(summary, '.cd-esolver-bill__row--subtotal', formatMoney(model.subtotal));
    renderDiscounts(summary, model.discounts);
    setBillValue(summary, '.cd-esolver-bill__row--shipping', config.shippingValue);
    setBillValue(summary, '.cd-esolver-bill__row--total', formatMoney(model.total));
    summary.querySelector('.cd-esolver-bill__row--total .cd-esolver-bill__label').textContent = model.estimated ? config.estimatedTotalLabel : config.totalLabel;
    var note = summary.querySelector('.cd-esolver-bill__note');
    note.textContent = model.estimated ? config.estimatedNote : '';
    note.hidden = !model.estimated;
  }

  function renderSummaryError(root) {
    var summary = root.querySelector('.cd-esolver-summary');
    if (!summary) return;
    summary.dataset.summaryStatus = 'error';
    summary.querySelector('.cd-esolver-summary-card__count').textContent = '';
    summary.querySelector('.cd-esolver-summary-card__total').textContent = config.summaryUnavailable;
    summary.querySelector('.cd-esolver-summary-card__offer').hidden = true;
    summary.querySelector('.cd-esolver-summary-card__savings').hidden = true;
  }

  function cartUrl() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
    return root.replace(/\/$/, '') + '/cart.js';
  }

  function refreshSummary(root) {
    var requestId = ++summaryRequestId;
    var pending = pendingProductLine(false);
    var summary = root.querySelector('.cd-esolver-summary');
    if (summary) summary.dataset.summaryStatus = 'loading';

    window.fetch(cartUrl(), { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('cart');
        return response.json();
      })
      .then(function (cart) {
        if (requestId !== summaryRequestId || root !== observedRoot) return;
        if (!pending && Number(cart.item_count) === 0) pending = pendingProductLine(true);
        renderSummary(root, summaryModel(cart, pending));
      })
      .catch(function () {
        if (requestId === summaryRequestId && root === observedRoot) renderSummaryError(root);
      });
  }

  function createTrustFooter() {
    var footer = createElement('div', 'cd-esolver-trust');
    [config.trustVerification, config.trustSms, config.trustCheckout, config.trustNumber]
      .filter(Boolean)
      .forEach(function (label) {
        footer.appendChild(createTrustItem(label));
      });
    return footer;
  }

  function syncCopy(root) {
    var codeStep = root.querySelector('#step-code');
    var title = root.querySelector('.otp-title');
    var subtitle = root.querySelector('.otp-subtitle');
    if (!codeStep || !title || !subtitle) return;

    var codeVisible = !codeStep.classList.contains('hidden') && getComputedStyle(codeStep).display !== 'none';
    var nextTitle = codeVisible ? config.codeHeading : config.phoneHeading;
    var nextSubtitle = codeVisible ? config.codeSubtitle : config.phoneSubtitle;
    if (title.textContent !== nextTitle) title.textContent = nextTitle;
    if (subtitle.textContent !== nextSubtitle) subtitle.textContent = nextSubtitle;
  }

  function buildShell(root) {
    if (!root) return false;

    var box = root.querySelector('.otp-box');
    var closeButton = root.querySelector('#btn-close');
    var title = root.querySelector('.otp-title');
    var subtitle = root.querySelector('.otp-subtitle');
    var phoneStep = root.querySelector('#step-phone');
    var codeStep = root.querySelector('#step-code');
    var error = root.querySelector('#otp-error');
    if (!box || !closeButton || !title || !subtitle || !phoneStep || !codeStep || !error) return false;

    if (!box.querySelector('.cd-esolver-header')) {
      var header = createHeader(closeButton);
      var notice = createElement('div', 'cd-esolver-notice', config.notice);
      var main = createElement('div', 'cd-esolver-main');
      var heading = createElement('div', 'cd-esolver-heading');

      heading.appendChild(title);
      heading.appendChild(subtitle);
      main.appendChild(createOrderSummary());
      main.appendChild(heading);
      main.appendChild(phoneStep);
      main.appendChild(codeStep);
      main.appendChild(error);

      box.replaceChildren(header, notice, main, createTrustFooter());
    }

    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    title.id = 'cd-esolver-otp-title';
    box.setAttribute('aria-labelledby', title.id);
    root.setAttribute(SHELL_ATTRIBUTE, 'true');
    syncCopy(root);
    return true;
  }

  function inspect() {
    scheduled = false;
    var root = document.getElementById(ROOT_ID);

    if (root !== observedRoot) {
      if (rootObserver) rootObserver.disconnect();
      observedRoot = root;
      rootObserver = null;
      lastVisible = false;

      if (observedRoot) {
        rootObserver = new MutationObserver(scheduleInspection);
        rootObserver.observe(observedRoot, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style'],
        });
      }
    }

    if (!buildShell(root)) return;

    var visible = getComputedStyle(root).display !== 'none';
    if (visible && !lastVisible) refreshSummary(root);
    lastVisible = visible;
  }

  function scheduleInspection() {
    if (scheduled) return;
    scheduled = true;
    window.setTimeout(inspect, 0);
  }

  function rememberTriggerProductForm(event) {
    triggerProductForm = event.target && event.target.closest
      ? event.target.closest('form[action*="/cart/add"]')
      : null;
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      cartDiscounts: cartDiscounts,
      formatMoney: formatMoney,
      offerSummary: offerSummary,
      summaryModel: summaryModel,
    };
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('pointerdown', rememberTriggerProductForm, true);
    var observer = new MutationObserver(scheduleInspection);
    observer.observe(document.body || document.documentElement, { childList: true });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', scheduleInspection, { once: true });
    } else {
      scheduleInspection();
    }
  }
})();
